<template>
  <popup-dialog
    :visible="textEditor.value != null"
    :close-button-visible="true"
    :resizable="true"
    header-class="textviewer__header"
    class="textviewer"
    :class="{ 'textviewer--linksactive': linkKeyActive }"
    @show="onShow()"
    @close="onClose()"
  >
    <template #header>
      <text-editor-menu :editor="editor" @link-created="onLinkCreated" />
    </template>

    <!-- The text editor field itself -->
    <editor-content
      ref="editorContentElement"
      class="texteditor__container"
      :editor="editor"
    />
    <text-editor-link-menu ref="linkMenu" :editor="editor" />
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch, onBeforeUnmount } from "vue";
import { useTexteditor } from "@core/ui/textEditorPopup.store";

// Import the editor
import { Editor, EditorContent } from "@tiptap/vue-3";
import { Editor as TiptapEditor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";

import { TextEditorLink } from "@core/input/TextEditorLink";
import TextEditorMenu from "@core/input/TextEditorMenu.vue";
import TextEditorLinkMenu from "@core/input/TextEditorLinkMenu.vue";
import PopupDialog from "./PopupDialog.vue";
import { onKeyStroke } from "@vueuse/core";

import type { EditorOptions } from "@tiptap/core";

const editor = ref<Editor>();
const editorContentElement = ref<InstanceType<typeof EditorContent>>();
const linkMenu = ref<InstanceType<typeof TextEditorLinkMenu> | null>(null);

const textEditor = useTexteditor();

const editorOptions = computed((): Partial<EditorOptions> => {
  return {
    autofocus: true,
    editorProps: {
      attributes: {
        class: "textviewer__field formatted-text"
      }
    },
    content: textEditor.value,
    extensions: [
      StarterKit.configure({
        // Disable the default link extension
        link: false
      }),
      TextEditorLink.configure({
        autolink: true,
        linkOnPaste: true
      }),
      TaskList,
      TaskItem,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ["start", "center", "end"],
        defaultAlignment: "start"
      }),
      TextStyleKit
    ],
    onUpdate: onInput
  };
});

const linkKeyActive = computed(() => {
  for (const key in linkTriggerKeys) {
    if (linkTriggerKeys[key]) return true;
  }
  return false;
});

watch(
  () => textEditor.value,
  (val) => {
    if (editor.value != null) {
      if (editor.value.getHTML() !== val) {
        editor.value.commands.setContent(textEditor.value || "");
      }
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unbindEditor();
});

onKeyStroke(["Escape"], () => {
  textEditor.close();
});

function focus() {
  if (editor.value == null) return;
  editor.value.chain().focus().run();
}

function blur() {
  if (editor.value == null) return;
  editor.value.chain().blur().run();
}

function onInput({ editor }: { editor: TiptapEditor }) {
  if (textEditor.onUpdate) {
    const newText = editor.getHTML();
    textEditor.onUpdate(newText);
  }
}

function onLinkCreated(href: string) {
  if (!linkMenu.value) return;
  linkMenu.value.showLinkMenu(href);
}

function onShow() {
  if (editor.value) {
    editor.value.destroy();
  }
  editor.value = new Editor(editorOptions.value);
  bindLinkTriggerKey();
}

function onClose() {
  unbindLinkTriggerKey();
  textEditor.close();
  unbindEditor();
}

function unbindEditor() {
  if (editor.value) {
    // Workaround for bug in tiptap - see https://github.com/ueberdosis/tiptap/issues/2296
    // To remove when bug is fixed.
    const staticEditorContent = document.createElement("div");
    const editorContentEl = editorContentElement.value?.$el;
    if (editorContentEl) {
      editorContentEl.after(staticEditorContent);
      staticEditorContent.outerHTML = editorContentEl.outerHTML; // Set new element to contents of old one
      editorContentEl.style.display = "none";
    }

    editor.value.destroy();
    editor.value = undefined;
  }
}

// ==========================
// Handle Keyboard Shortcuts

const linkTriggerKeys: Record<string, boolean> = reactive({
  Control: false,
  Alt: false,
  Meta: false
});

function onKeyDown(event: KeyboardEvent) {
  if (event.key in linkTriggerKeys) {
    linkTriggerKeys[event.key] = true;
  }
}

function onKeyUp(event: KeyboardEvent) {
  if (event.key in linkTriggerKeys) {
    linkTriggerKeys[event.key] = false;
  }
}

function bindLinkTriggerKey() {
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
}

function unbindLinkTriggerKey() {
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("keyup", onKeyUp);
}

// ==========================
// EXPOSE

defineExpose({
  focus,
  blur,
  close: textEditor.close
});
</script>

<style lang="scss">
.textviewer {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content 1fr;
  font-size: 22px;
  padding: 2px;
  padding-top: 0; // prevent gap between header and content

  box-shadow: 0 0 1rem var(--color-shadow-2);
  border-radius: 3px;
  background-color: var(--color-background-deeper);
  overflow: auto;
  backface-visibility: hidden;

  height: 60rem;
  max-height: 100%;
  width: 72ch;

  &__background {
    background-color: var(--color-backdrop);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  &__content {
    width: 100%;
    height: 100%;
    text-align: left;
    max-width: 100vw;
    min-height: 100%;
    padding: 0;
  }

  &__header {
    position: sticky;
    top: 0;
    background-color: var(--color-background-deep);
    z-index: 1;
  }

  &__field {
    height: 100%;
    padding: 2rem 2rem;
    padding-top: 3rem;
    appearance: none;
    line-height: 1.6em;
    min-height: 25vh;
    font-size: 16px;

    h1,
    h2,
    h3 {
      line-height: 1.2em;
    }

    h1 {
      font-size: 24px;
      font-weight: 600;
    }
    h2 {
      font-size: 20px;
      font-weight: 600;
    }
    h3 {
      font-size: 18px;
      font-weight: 600;
    }

    p {
      font-size: 16px;
      line-height: 1.4;
    }

    li > p {
      margin-bottom: 0.2em;
    }

    &:focus {
      outline: none;
    }

    @media screen and (min-width: 481px) {
      padding-inline: 6rem;
    }
  }

  &--linksactive &__field a {
    cursor: pointer;
    text-decoration: underline;
  }

  &__editor {
    min-height: 100%;
  }

  .popup__close {
    z-index: 2;
  }
}
</style>
